// Port of the digital-certificate module's templateCoordinate.ts (buildCoordinatesPayload etc.) so the
// saved JSON is the same schema: flat items in PDF points, `title` is the data key.

export const QR_FIELD_NAME = 'QR_CODE';
export const FONT_FAMILY_OPTIONS = ['Helvetica', 'Times New Roman', 'Arial'];
// Field names (titles) come from the backend: GET /accred-printing/fields (the Add-User form's DB columns)

export const getDefaultFieldTitle = (type, number) => {
  if (type === 'qr') return QR_FIELD_NAME;
  if (type === 'image') return `Image ${number}`;
  return `Field ${number}`;
};

export const getFieldDisplayTitle = (field) => field.title.trim() || getDefaultFieldTitle(field.type, field.number);

export const buildCoordinatesPayload = (fields) =>
  fields.map((field) => {
    const base = {
      x: field.x,
      y: field.y,
      type: field.type,
      title: field.title.trim() || getDefaultFieldTitle(field.type, field.number),
    };

    if (field.type === 'text') {
      const size = String(field.fontSize ?? 14);
      const family = field.fontFamily ?? 'Helvetica';
      const out = { ...base, fontSize: size, font_size: size, fontFamily: family, font_family: family };

      const width = field.width ?? field.boxWidth ?? 180;
      const endX = field.endX ?? (field.x != null ? Math.round(field.x + width) : undefined);
      out.width = width;
      out.boxWidth = width;
      out.box_width = width;
      if (endX != null) {
        out.endX = endX;
        out.end_x = endX;
      }
      // text sits centred between Start X and End X unless another alignment is chosen
      const align = field.textAlign ?? 'center';
      out.textAlign = align;
      out.text_align = align;
      if (field.maxLines != null && field.maxLines > 1) {
        out.maxLines = field.maxLines;
        out.max_lines = field.maxLines;
      }
      if (field.minFontSize != null) {
        out.minFontSize = field.minFontSize;
        out.min_font_size = field.minFontSize;
      }
      if (field.row2X != null) { out.row2X = field.row2X; out.row2_x = field.row2X; }
      if (field.row2EndX != null) { out.row2EndX = field.row2EndX; out.row2_end_x = field.row2EndX; }
      if (field.row2Y != null) { out.row2Y = field.row2Y; out.row2_y = field.row2Y; }
      if (field.lineSpacing != null) { out.lineSpacing = field.lineSpacing; out.line_spacing = field.lineSpacing; }
      return out;
    }

    const width = field.imgWidth ?? 250;
    const height = field.imgHeight ?? 100;
    return { ...base, imgWidth: width, img_width: width, imgHeight: height, img_height: height };
  });

// Saved items -> editable fields (either camelCase or snake_case keys may be present)
export const fieldsFromPayload = (items) =>
  (Array.isArray(items) ? items : []).map((it, i) => ({
    id: `f_${Date.now()}_${i}`,
    number: i + 1,
    x: Number(it.x),
    y: Number(it.y),
    type: it.type,
    title: it.title || '',
    fontSize: it.fontSize ?? it.font_size,
    fontFamily: it.fontFamily ?? it.font_family,
    textAlign: it.textAlign ?? it.text_align,
    width: it.width ?? it.boxWidth ?? it.box_width,
    endX: it.endX ?? it.end_x,
    maxLines: it.maxLines ?? it.max_lines,
    minFontSize: it.minFontSize ?? it.min_font_size,
    row2X: it.row2X ?? it.row2_x,
    row2EndX: it.row2EndX ?? it.row2_end_x,
    row2Y: it.row2Y ?? it.row2_y,
    lineSpacing: it.lineSpacing ?? it.line_spacing,
    imgWidth: it.imgWidth ?? it.img_width,
    imgHeight: it.imgHeight ?? it.img_height,
  }));

export function demo() {
  const text = { id: 'a', number: 1, x: 40, y: 100, type: 'text', title: ' Name ', fontSize: '20', fontFamily: 'Arial', maxLines: 2, textAlign: 'center' };
  const img = { id: 'b', number: 2, x: 10, y: 20, type: 'image', title: '', imgWidth: 80, imgHeight: 90 };
  const qr = { id: 'c', number: 3, x: 1, y: 2, type: 'qr', title: QR_FIELD_NAME };
  const [t, i, q] = buildCoordinatesPayload([text, img, qr]);
  console.assert(t.title === 'Name' && t.fontSize === '20' && t.font_size === '20' && t.endX === 220 && t.end_x === 220, 'text keys (endX = x + default width 180)');
  console.assert(t.maxLines === 2 && t.max_lines === 2 && t.textAlign === 'center', 'overflow keys only when set');
  console.assert(!('row2X' in t) && !('minFontSize' in t), 'unset keys are omitted');
  console.assert(i.title === 'Image 2' && i.imgWidth === 80 && i.img_height === 90, 'image keys + default title');
  console.assert(q.imgWidth === 250 && q.title === 'QR_CODE', 'qr defaults');
  const back = fieldsFromPayload([t, i, q]);
  console.assert(back[0].fontSize === '20' && back[0].maxLines === 2 && back[0].width === 180 && back[1].imgHeight === 90, 'round trip');
  console.log('templateCoordinate ok');
}
